import * as React from 'react'
import { useContext, useMemo, useState } from 'react'
import { CompoundButton, Text } from '@fluentui/react-components'
import * as UUID from 'uuid'
import AlertDialog from '../../../components/Dialog/AlertDialog'
import { LegalAidCertificateInvoiceProps } from './Invoices'
import { useFormikContext } from 'formik'
import { LegalAidCertificateDto } from './LegalAidCertificateForm'
import { useThemeClassName_unstable } from '@fluentui/react-shared-contexts'
import { formatCurrencyDecimals } from '../../../helpers/numberHelper'
import { InvoiceDto, LineItemType } from '../../../config/finance/invoiceConfig'
import usePersistLegalAidCertificateInvoice, { PrefillLineItems } from '../../../hooks/finance/invoice/usePersistLegalAidCertificateInvoice'
import GlobalContext, { GlobalContextType } from '../../../context/GlobalContext'
import useCurrency from '../../../hooks/useCurrency'
import { bundleIcon, DocumentQueueAddFilled, DocumentQueueAddRegular } from '@fluentui/react-icons'
import useEntity from '../../../hooks/useEntity'
import { QueueStatus } from '../../../hooks/useQueueStateHandler'
import useSubmitInvoiceDocumentGeneration from '../../../hooks/finance/invoice/useSubmitInvoiceDocumentGeneration'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'

const DocumentQueueIcon = bundleIcon(DocumentQueueAddFilled, DocumentQueueAddRegular)

export default function CreateSettlementInvoiceButton(props: LegalAidCertificateInvoiceProps & { canCreateInvoice: boolean }) {
    const {
        settings: { legalAidCertificate: settings },
    } = useContext<GlobalContextType>(GlobalContext)
    const { values, setFieldValue, submitForm } = useFormikContext<LegalAidCertificateDto>()
    const requestId = useMemo(() => UUID.v4(), [props.selected?.id, values.settlementInvoiceId])
    const { currency } = useCurrency(values.currency)
    const persist = usePersistLegalAidCertificateInvoice(props.file, props.parties)
    const generatePdf = useSubmitInvoiceDocumentGeneration(DocumentGenerationType.Invoice)

    const [contributionInvoice] = useEntity<InvoiceDto>((id: string) => ({ url: `/finance/invoice/${id}` }), values.contributionInvoiceId)
    const [isAlertOpen, setAlertOpen] = useState(false)

    const themeClassName = useThemeClassName_unstable()

    const canCreateSettlementInvoice = useMemo<boolean>(
        () => !values.settlementInvoiceId && values.contributionInvoiceId && contributionInvoice.loaded && props.canCreateInvoice,
        [props.canCreateInvoice, values.settlementInvoiceId, values.contributionInvoiceId, contributionInvoice.loaded],
    )

    const createSettlementInvoice = (prefillLineItems?: PrefillLineItems) => {
        return persist(
            requestId,
            {
                templateId: settings.settlementInvoice.templateId,
                clientPivotId: values.clientPivotId,
                debtorPivotId: values.legalAidBoardPivotId,
                currency: values.currency,
                recipientReference: values.reference,
            },
            data => {
                generatePdf(data, {
                    [QueueStatus.Dispatch]: () => {
                        setAlertOpen(false)
                    },
                    [QueueStatus.Finished]: () => {
                        setFieldValue('settlementInvoiceId', data.invoiceId)
                        submitForm()
                    },
                })
            },
            {
                [settings.settlementInvoice.feeTemplateId]: formatCurrencyDecimals(
                    Number(values.baseFee) + Number(values.travelFee) + Number(values.fixedCompensation),
                    currency,
                    'en',
                    'en',
                    false,
                ),
                [settings.settlementInvoice.travelTemplateId]: values.travelExpenses,
            },
            prefillLineItems,
        )
    }

    return (
        <>
            <CompoundButton
                disabled={!canCreateSettlementInvoice}
                secondaryContent="Verwerk vergoeding RvR"
                appearance="subtle"
                icon={<DocumentQueueIcon />}
                onClick={() => {
                    if (contributionInvoice.data.credit?.length > 0) {
                        setAlertOpen(true)
                    } else {
                        createSettlementInvoice({
                            [settings.settlementInvoice.contributionTemplateId]: [
                                {
                                    type: LineItemType.Invoice,
                                    entityId: values.contributionInvoiceId,
                                },
                            ],
                        })
                    }
                }}
            >
                Eindafrekening RvR
            </CompoundButton>
            <AlertDialog
                className={themeClassName}
                content={
                    <>
                        <Text block>
                            De declaratie voor de eigen bijdrage is al verrekend in declaratie(s)&nbsp;
                            <Text weight="semibold">{contributionInvoice.data?.credit?.map(item => item.number).join(', ')}</Text>.
                        </Text>
                        <Text block>Wilt u doorgaan zonder verrekening van de eigen bijdrage?</Text>
                    </>
                }
                onConfirm={() => createSettlementInvoice()}
                setOpen={setAlertOpen}
                open={isAlertOpen}
            />
        </>
    )
}
